import { NOTIFICATION_TIMEOUT, showNotification } from '../../notifications';
import { getCurrentConference } from '../conference/functions';
import { set } from '../redux';

import {
    DOMINANT_SPEAKER_CHANGED,
    GRANT_MODERATOR,
    HIDDEN_PARTICIPANT_JOINED,
    HIDDEN_PARTICIPANT_LEFT,
    HIDE_RECORDING_CONSENT_MODAL,
    KICK_PARTICIPANT,
    LOCAL_PARTICIPANT_RAISE_HAND,
    MUTE_REMOTE_PARTICIPANT,
    PARTICIPANT_DESIGNATION,
    PARTICIPANT_ID_CHANGED,
    PARTICIPANT_JOINED,
    PARTICIPANT_KICKED,
    PARTICIPANT_LEFT,
    PARTICIPANT_ORGANIZATION,
    PARTICIPANT_UPDATED,
    PARTICIPANT_VOLUME_CHANGED,
    PIN_PARTICIPANT,
    RAISE_HAND_CLEAR,
    RAISE_HAND_UPDATED,
    RECORDING_STARTED_BY_USER,
    RESET_RECORDING_STARTED_BY_USER,
    SCREENSHARE_PARTICIPANT_NAME_CHANGED,
    SET_LOADABLE_AVATAR_URL,
    SET_LOCAL_PARTICIPANT_RECORDING_STATUS,
    SET_MODERATOR_PIN,
    SET_PARTICIPANT_COLOR,
    SHOW_RECORDING_CONSENT_MODAL
} from './actionTypes';
import { DISCO_REMOTE_CONTROL_FEATURE } from './constants';
import {
    getLocalParticipant,
    getNormalizedDisplayName,
    getParticipantById,
    getParticipantDisplayName
} from './functions';
import logger from './logger';

/**
 * Create an action for when dominant speaker changes.
 *
 * @param {string} id - Participant's ID.
 * @param {MHConference} conference - The {@code MHConference} associated
 * with the participant identified by the specified {@code id}. Only the local
 * participant is allowed to not specify an associated {@code MHConference}
 * instance.
 * @returns {{
 *     type: DOMINANT_SPEAKER_CHANGED,
 *     participant: {
 *         conference: MHConference,
 *         id: string
 *     }
 * }}
 */
export function dominantSpeakerChanged(id, conference) {
    return {
        type: DOMINANT_SPEAKER_CHANGED,
        participant: {
            conference,
            id
        }
    };
}

/**
 * Create an action for granting moderator to a participant.
 *
 * @param {string} id - Participant's ID.
 * @returns {{
 *     type: GRANT_MODERATOR,
 *     id: string
 * }}
 */
export function grantModerator(id) {
    return {
        type: GRANT_MODERATOR,
        id
    };
}

/**
 * Create an action for removing a participant from the conference.
 *
 * @param {string} id - Participant's ID.
 * @param {string | undefined} reason - Participant's ID.
 * @returns {{
 *     type: KICK_PARTICIPANT,
 *     id: string
 * }}
 */
export function kickParticipant(id, reason = undefined) {
    return {
        type: KICK_PARTICIPANT,
        id,
        reason
    };
}

/**
 * Creates an action to signal the connection status of the local participant
 * has changed.
 *
 * @param {string} connectionStatus - The current connection status of the local
 * participant, as enumerated by the library's participantConnectionStatus
 * constants.
 * @returns {Function}
 */
export function localParticipantConnectionStatusChanged(connectionStatus) {
    return (dispatch, getState) => {
        const participant = getLocalParticipant(getState);

        if (participant) {
            return dispatch(
        participantConnectionStatusChanged(participant.id, connectionStatus)
            );
        }
    };
}

/**
 * Action to signal that the ID of local participant has changed. It happens
 * when the local participant joins a new conference or leaves an existing
 * conference.
 *
 * @param {string} id - New ID for local participant.
 * @returns {Function}
 */
export function localParticipantIdChanged(id) {
    return (dispatch, getState) => {
        const participant = getLocalParticipant(getState);

        if (participant) {
            return dispatch({
                type: PARTICIPANT_ID_CHANGED,

                // XXX A participant is identified by an id-conference pair.
                // Only the local participant is with an undefined conference.
                conference: undefined,
                newValue: id,
                oldValue: participant.id
            });
        }
    };
}

/**
 * Action to signal that a local participant has joined.
 *
 * @param {Participant} participant={} - Information about participant.
 * @returns {{
 *     type: PARTICIPANT_JOINED,
 *     participant: Participant
 * }}
 */
export function localParticipantJoined(participant = {}) {
    return participantJoined(set(participant, 'local', true));
}

/**
 * Action to remove a local participant.
 *
 * @returns {Function}
 */
export function localParticipantLeft() {
    return (dispatch, getState) => {
        const participant = getLocalParticipant(getState);

        if (participant) {
            return dispatch(
        participantLeft(
          participant.id,

          // XXX Only the local participant is allowed to leave
          // without stating the MHConference instance because
          // the local participant is uniquely identified by the
          // very fact that there is only one local participant
          // (and the fact that the local participant "joins" at
          // the beginning of the app and "leaves" at the end of
          // the app).
          undefined
        )
            );
        }
    };
}

/**
 * Action to signal the role of the local participant has changed. It can happen
 * when the participant has joined a conference, even before a non-default local
 * id has been set, or after a moderator leaves.
 *
 * @param {string} role - The new role of the local participant.
 * @returns {Function}
 */
export function localParticipantRoleChanged(role) {
    return (dispatch, getState) => {
        const participant = getLocalParticipant(getState);

        if (participant) {
            return dispatch(participantRoleChanged(participant.id, role));
        }
    };
}

/**
 * Action to set the designation of the local participant.
 *
 * @param {string} designation - The designation to set.
 * @returns {Function}
 */
export function setLocalParticipantDesignation(designation) {
    return (dispatch, getState) => {
        const participant = getLocalParticipant(getState);

        if (participant) {
            return dispatch(participantDesignation(participant.id, designation));
        }
    };
}

/**
 * Action to set the organization of the local participant.
 *
 * @param {string} organization - The organization to set.
 * @returns {Function}
 */
export function setLocalParticipantOrganization(organization) {
    return (dispatch, getState) => {
        const participant = getLocalParticipant(getState);

        if (participant) {
            return dispatch(participantOrganization(participant.id, organization));
        }
    };
}

/**
 * Create an action for muting another participant in the conference.
 *
 * @param {string} id - Participant's ID.
 * @param {MEDIA_TYPE} mediaType - The media to mute.
 * @returns {{
 *     type: MUTE_REMOTE_PARTICIPANT,
 *     id: string,
 *     mediaType: MEDIA_TYPE
 * }}
 */
export function muteRemoteParticipant(id, mediaType) {
    return {
        type: MUTE_REMOTE_PARTICIPANT,
        id,
        mediaType
    };
}

/**
 * Action to update a participant's connection status.
 *
 * @param {string} id - Participant's ID.
 * @param {string} connectionStatus - The new connection status of the
 * participant.
 * @returns {{
 *     type: PARTICIPANT_UPDATED,
 *     participant: {
 *         connectionStatus: string,
 *         id: string
 *     }
 * }}
 */
export function participantConnectionStatusChanged(id, connectionStatus) {
    return {
        type: PARTICIPANT_UPDATED,
        participant: {
            connectionStatus,
            id
        }
    };
}

/**
 * Action to signal that a participant has joined.
 *
 * @param {Participant} participant - Information about participant.
 * @returns {{
 *     type: PARTICIPANT_JOINED,
 *     participant: Participant
 * }}
 */
export function participantJoined(participant) {
    // Only the local participant is not identified with an id-conference pair.
    if (participant.local) {
        return {
            type: PARTICIPANT_JOINED,
            participant
        };
    }

    // In other words, a remote participant is identified with an id-conference
    // pair.
    const { conference } = participant;

    if (!conference) {
        throw Error('A remote participant must be associated with a MHConference!');
    }

    return (dispatch, getState) => {
    // A remote participant is only expected to join in a joined or joining
    // conference. The following check is really necessary because a
    // MHConference may have moved into leaving but may still manage to
    // sneak a PARTICIPANT_JOINED in if its leave is delayed for any purpose
    // (which is not outragous given that leaving involves network
    // requests.)
        const stateFeaturesBaseConference = getState()['features/base/conference'];

        if (
            conference === stateFeaturesBaseConference.conference
      || conference === stateFeaturesBaseConference.joining
        ) {
            return dispatch({
                type: PARTICIPANT_JOINED,
                participant
            });
        }
    };
}

/**
 * Updates the features of a remote participant.
 *
 * @param {MHParticipant} jitsiParticipant - The ID of the participant.
 * @returns {{
 *     type: PARTICIPANT_UPDATED,
 *     participant: Participant
 * }}
 */
export function updateRemoteParticipantFeatures(jitsiParticipant) {
    return (dispatch, getState) => {
        if (!jitsiParticipant) {
            return;
        }

        const id = jitsiParticipant.getId();

        jitsiParticipant
      .getFeatures()
      .then(features => {
          const supportsRemoteControl = features.has(
          DISCO_REMOTE_CONTROL_FEATURE
          );
          const participant = getParticipantById(getState(), id);

          if (!participant || participant.local) {
              return;
          }

          if (participant?.supportsRemoteControl !== supportsRemoteControl) {
              return dispatch({
                  type: PARTICIPANT_UPDATED,
                  participant: {
                      id,
                      supportsRemoteControl
                  }
              });
          }
      })
      .catch(error => {
          logger.error(`Failed to get participant features for ${id}!`, error);
      });
    };
}

/**
 * Action to signal that a hidden participant has joined the conference.
 *
 * @param {string} id - The id of the participant.
 * @param {string} displayName - The display name, or undefined when
 * unknown.
 * @returns {{
 *     type: HIDDEN_PARTICIPANT_JOINED,
 *     displayName: string,
 *     id: string
 * }}
 */
export function hiddenParticipantJoined(id, displayName) {
    return {
        type: HIDDEN_PARTICIPANT_JOINED,
        id,
        displayName
    };
}

/**
 * Action to signal that a hidden participant has left the conference.
 *
 * @param {string} id - The id of the participant.
 * @returns {{
 *     type: HIDDEN_PARTICIPANT_LEFT,
 *     id: string
 * }}
 */
export function hiddenParticipantLeft(id) {
    return {
        type: HIDDEN_PARTICIPANT_LEFT,
        id
    };
}

/**
 * Action to signal that a participant has left.
 *
 * @param {string} id - Participant's ID.
 * @param {MHConference} conference - The {@code MHConference} associated
 * with the participant identified by the specified {@code id}. Only the local
 * participant is allowed to not specify an associated {@code MHConference}
 * instance.
 * @returns {{
 *     type: PARTICIPANT_LEFT,
 *     participant: {
 *         conference: MHConference,
 *         id: string
 *     }
 * }}
 */
export function participantLeft(id, conference) {
    return {
        type: PARTICIPANT_LEFT,
        participant: {
            conference,
            id
        }
    };
}

/**
 * Action to signal that a participant's presence status has changed.
 *
 * @param {string} id - Participant's ID.
 * @param {string} presence - Participant's new presence status.
 * @returns {{
 *     type: PARTICIPANT_UPDATED,
 *     participant: {
 *         id: string,
 *         presence: string
 *     }
 * }}
 */
export function participantPresenceChanged(id, presence) {
    return participantUpdated({
        id,
        presence
    });
}

/**
 * The type of Redux action which stores the participants designation.
 *
 * @param {string} id - The id of the participant.
 * @returns {{
 *     type: PARTICIPANT_DESIGNATION,
 *     designation: string
 * }}
 */
export function participantDesignation(id, designation) {

    return {
        type: PARTICIPANT_DESIGNATION,
        id,
        designation
    };
}

/**
 * The type of Redux action which stores the participants organization.
 *
 * @param {string} id - The id of the participant.
 * @param {string} organization - The organization of the participant.
 * @returns {{
 *     type: PARTICIPANT_ORGANIZATION,
 *     organization: string,
 *     id: string
 * }}
 */
export function participantOrganization(id, organization) {

    return {
        type: PARTICIPANT_ORGANIZATION,
        id,
        organization
    };
}

/**
 * Action to signal that a participant's role has changed.
 *
 * @param {string} id - Participant's ID.
 * @param {PARTICIPANT_ROLE} role - Participant's new role.
 * @returns {{
 *     type: PARTICIPANT_UPDATED,
 *     participant: {
 *         id: string,
 *         role: PARTICIPANT_ROLE
 *     }
 * }}
 */
export function participantRoleChanged(id, role) {
    return participantUpdated({
        id,
        role
    });
}

/**
 * Action to signal that some of participant properties has been changed.
 *
 * @param {Participant} participant={} - Information about participant. To
 * identify the participant the object should contain either property id with
 * value the id of the participant or property local with value true (if the
 * local participant hasn't joined the conference yet).
 * @returns {{
 *     type: PARTICIPANT_UPDATED,
 *     participant: Participant
 * }}
 */
export function participantUpdated(participant = {}) {
    const participantToUpdate = {
        ...participant
    };

    // console.log(participantToUpdate, 'upToDate==');

    if (participant.name) {
        participantToUpdate.name = getNormalizedDisplayName(participant.name);
    }

    if (participant.designation) {
        participantToUpdate.designation = participant.designation;
    }

    if (participant.organization) {
        participantToUpdate.organization = participant.organization;
    }

    return {
        type: PARTICIPANT_UPDATED,
        participant: participantToUpdate
    };
}

/**
 * Action to signal that a participant has muted us.
 *
 * @param {MHParticipant} participant - Information about participant.
 * @param {MHLocalTrack} track - Information about the track that has been muted.
 * @returns {Promise}
 */
export function participantMutedUs(participant, track) {
    return (dispatch, getState) => {
        if (!participant) {
            return;
        }

        const isAudio = track.isAudioTrack();


        // Add a small delay to allow force mute state to be received
        setTimeout(() => {
            // Get the latest state to ensure we have the most up-to-date force mute status
            const currentState = getState();
            const { lockAudio, lockVideo } = currentState['features/base/conference'];
            const conference = getCurrentConference(currentState);
            const localParticipantId = conference?.myUserId();

            const isAudioForceMuted = isAudio && lockAudio?.[localParticipantId] === true;
            const isVideoForceMuted = !isAudio && lockVideo?.[localParticipantId] === true;

            if (isAudioForceMuted || isVideoForceMuted) {
                return;
            }

            dispatch(
                showNotification({
                    descriptionKey: isAudio
                        ? 'notify.mutedRemotelyDescription'
                        : 'notify.videoMutedRemotelyDescription',
                    titleKey: isAudio
                        ? 'notify.mutedRemotelyTitle'
                        : 'notify.videoMutedRemotelyTitle',
                    titleArguments: {
                        participantDisplayName: getParticipantDisplayName(
                            getState,
                            participant.getId()
                        )
                    }
                })
            );
        }, 500); // 500ms delay to allow force mute state to be received
    };
}

/**
 * Action to signal that a participant had been kicked.
 *
 * @param {MHParticipant} kicker - Information about participant performing the kick.
 * @param {MHParticipant} kicked - Information about participant that was kicked.
 * @returns {Promise}
 */
export function participantKicked(kicker, kicked) {
    return (dispatch, getState) => {
        dispatch({
            type: PARTICIPANT_KICKED,
            kicked: kicked.getId(),
            kicker: kicker.getId()
        });

        dispatch(
      showNotification(
        {
            titleArguments: {
                kicked: getParticipantDisplayName(getState, kicked.getId()),
                kicker: getParticipantDisplayName(getState, kicker.getId())
            },
            titleKey: 'notify.kickParticipant'
        },
        NOTIFICATION_TIMEOUT * 2
      )
        ); // leave more time for this
    };
}

/**
 * Create an action which pins a conference participant.
 *
 * @param {string|null} id - The ID of the conference participant to pin or null
 * if none of the conference's participants are to be pinned.
 * @returns {{
 *     type: PIN_PARTICIPANT,
 *     participant: {
 *         id: string,
 *        isPinned: boolean
 *     }
 * }}
 */
export function pinParticipant(id, isPinned) {

    return {
        type: PIN_PARTICIPANT,
        participant: {
            id,
            isPinned
        }
    };
}

/**
 * Create an action which stores which participant started the recording.
 *
 * @param {string|null} id - The ID of the conference participant to pin or null
 * if none of the conference's participants are to be pinned.
 * @returns {{
 *     type: RECORDING_STARTED_BY_USER,
 *     participant: {
 *         id: string
 *     }
 * }}
 */
export function recordingStartedByUser(id) {
    return {
        type: RECORDING_STARTED_BY_USER,
        participant: {
            id
        }
    };
}

/**
 * Resets the isRecordingStartedByUser flag on the local participant after
 * recording stops so the consent dialog fires correctly on the next recording.
 *
 * @param {string} id - The participant ID.
 * @returns {{
 *     type: RESET_RECORDING_STARTED_BY_USER,
 *     participant: { id: string }
 * }}
 */
export function resetRecordingStartedByUser(id) {
    return {
        type: RESET_RECORDING_STARTED_BY_USER,
        participant: {
            id
        }
    };
}

/**
 * Creates an action which notifies the app that the loadable URL of the avatar of a participant got updated.
 *
 * @param {string} participantId - The ID of the participant.
 * @param {string} url - The new URL.
 * @returns {{
 *     type: SET_LOADABLE_AVATAR_URL,
 *     participant: {
 *         id: string,
 *         loadableAvatarUrl: string
 *     }
 * }}
 */
export function setLoadableAvatarUrl(participantId, url) {
    return {
        type: SET_LOADABLE_AVATAR_URL,
        participant: {
            id: participantId,
            loadableAvatarUrl: url
        }
    };
}

/**
 * Raise hand for the local participant.
 *
 * @param {boolean} enabled - Raise or lower hand.
 * @returns {{
 *     type: LOCAL_PARTICIPANT_RAISE_HAND,
 *     raisedHand: boolean,
 * }}
 */
export function raiseHand(enabled: boolean) {
    return {
        type: LOCAL_PARTICIPANT_RAISE_HAND,
        raisedHand: enabled
    };
}

/**
 * Clear the raise hand queue.
 *
 * @returns {{
 *     type: RAISE_HAND_CLEAR
 * }}
 */
export function raiseHandClear() {
    return {
        type: RAISE_HAND_CLEAR
    };
}

/**
 * Update raise hand queue of participants.
 *
 * @param {Object} participant - Participant that updated raised hand.
 * @returns {{
 *      type: RAISE_HAND_UPDATED,
 *      participant: Object
 * }}
 */
export function raiseHandUpdateQueue(participant = { id: '' }) {

    return {
        type: RAISE_HAND_UPDATED,
        id: participant.id
    };
}

/**
 * Action to signal that a participant's display name has changed.
 *
 * @param {string} id - Screenshare participant's ID.
 * @param {name} name - The new display name of the screenshare participant's owner.
 * @returns {{
 *     type: SCREENSHARE_PARTICIPANT_NAME_CHANGED,
 *     id: string,
 *     name: string
 * }}
 */
export function screenshareParticipantDisplayNameChanged(id, name) {
    return {
        type: SCREENSHARE_PARTICIPANT_NAME_CHANGED,
        id,
        name
    };
}

/**
 * Action to show recording consent modal to a specific participant.
 *
 * @param {string} participantId - The ID of the participant to show the modal to.
 * @returns {{
 *     type: SHOW_RECORDING_CONSENT_MODAL,
 *     participant: { id: string }
 * }}
 */
export function showRecordingConsentModal(participantId) {
    return {
        type: SHOW_RECORDING_CONSENT_MODAL,
        participant: { id: participantId }
    };
}

/**
 * Action to hide recording consent modal from a specific participant.
 *
 * @param {string} participantId - The ID of the participant to hide the modal from.
 * @returns {{
 *     type: HIDE_RECORDING_CONSENT_MODAL,
 *     participant: { id: string }
 * }}
 */
export function hideRecordingConsentModal(participantId) {
    return {
        type: HIDE_RECORDING_CONSENT_MODAL,
        participant: { id: participantId }
    };
}

/**
 *
 * Action to signal that a participant's color has changed.
 *
 * @param {string} id - Participant's ID.
 * @param {string} color - The new color of the participant.
 * @returns {{
 *     type: SET_PARTICIPANT_COLOR,
 *     id: string,
 *     color: string
 * }}
 */
export function setParticipantColor(id, color) {

    return {
        type: SET_PARTICIPANT_COLOR,
        id,
        color
    };
}


/**
 * Action to set a participant as moderator.
 *
 * @param {string} id - The ID of the participant to pin as moderator.
 * @returns {{
 *     type: SET_MODERATOR_PIN,
 *     participant: {
 *         id: string
 *     }
 * }}
 */
export function setModeratorPin(id, action = 'pin') {
    return {
        type: SET_MODERATOR_PIN,
        participant: {
            id,
            action // 'pin', 'unpin', or 'unpin_all'
        }
    };
}

/**
 * Action to signal that a participant's volume has changed.
 *
 * @param {string} id - Participant's ID.
 * @param {number} volume - The new volume of the participant.
 * @returns {{
 *     type: PARTICIPANT_VOLUME_CHANGED,
 *     id: string,
 *     volume: number
 * }}
 */
export function participantVolumeChanged(id, volume) {
    return {
        type: PARTICIPANT_VOLUME_CHANGED,
        id,
        volume
    };
}


/**
 * Local video recording status for the local participant.
 *
 * @param {boolean} recording - If local recording is ongoing.
 * @param {boolean} onlySelf - If recording only local streams.
 * @returns {{
 *     type: SET_LOCAL_PARTICIPANT_RECORDING_STATUS,
 *     recording: boolean
 * }}
 */
export function updateLocalRecordingStatus(recording, onlySelf) {
    return {
        type: SET_LOCAL_PARTICIPANT_RECORDING_STATUS,
        recording,
        onlySelf
    };
}
